/**
 * Story store – single source of truth for artist story (HTML content).
 * Same pattern as uspStore: ensureFetched on mount, update on save.
 */

import { create } from "zustand";
import { ArtistService } from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";
import type { StoryData, StoryPayload } from "@/services/artistService";

export type { StoryData, StoryPayload };

interface StoryState {
  data: StoryData | null;
  isLoading: boolean;
  saveLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: StoryData | null) => void;
  fetchStory: () => Promise<StoryData | null>;
  ensureFetched: () => Promise<StoryData | null>;
  updateStory: (payload: StoryPayload) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightFetch: Promise<StoryData | null> | null = null;

const initialState = {
  data: null,
  isLoading: false,
  saveLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useStoryStore = create<StoryState>()((set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchStory: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getStory();
        set({
          data,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return data;
      } catch (err) {
        set({ error: "Failed to load story", isLoading: false });
        return null;
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchStory, data } = get();
    if (hasFetchedInSession) return data;
    return fetchStory();
  },

  updateStory: async (payload) => {
    set({ saveLoading: true, error: null });
    try {
      await ArtistService.updateStory(payload);
      await get().fetchStory();
    } catch (err) {
      set({
        error: "Failed to save. Please try again.",
        saveLoading: false,
      });
      throw err;
    } finally {
      set({ saveLoading: false });
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}));
